/**
 * Generic read-only list page for tracking admin endpoints.
 * Displays title, optional search, a paginated table and empty/error states.
 */
import { Box, Flex, Heading, Input, InputGroup, InputRightElement, Spinner, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import type { ColumnDef } from "@tanstack/react-table";
import { Pagination } from "components/Pagination";

export interface TrackingApiListPageProps<T> {
  title: string;
  data: T[];
  isLoading: boolean;
  error: unknown;
  columns: ColumnDef<T, unknown>[];
  searchPlaceholder?: string;
  getSearchFilter?: (row: T, query: string) => boolean;
}

export default function TrackingApiListPage<T>({
  title,
  data,
  isLoading,
  error,
  columns,
  searchPlaceholder = "Rechercher",
  getSearchFilter,
}: TrackingApiListPageProps<T>) {
  const [searchValue, setSearchValue] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize] = useState(25);

  const filteredData = useMemo(() => {
    if (!getSearchFilter || !searchValue.trim()) return data;
    const q = searchValue.trim().toLowerCase();
    return data.filter((row) => getSearchFilter(row, q));
  }, [data, searchValue, getSearchFilter]);

  const numberOfPages = useMemo(
    () => Math.max(1, Math.ceil(filteredData.length / pageSize)),
    [filteredData.length, pageSize]
  );

  useEffect(() => {
    setPageIndex(1);
  }, [searchValue]);

  const pagedData = useMemo(() => {
    const start = (pageIndex - 1) * pageSize;
    const end = start + pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pageIndex, pageSize]);

  const handleNextPage = () => {
    setPageIndex((prev) => (prev < numberOfPages ? prev + 1 : prev));
  };

  const handlePrevPage = () => {
    setPageIndex((prev) => (prev > 1 ? prev - 1 : prev));
  };

  const handleJumpToPage = (page: number) => {
    if (!Number.isFinite(page)) return;
    if (page < 1) setPageIndex(1);
    else if (page > numberOfPages) setPageIndex(numberOfPages);
    else setPageIndex(page);
  };

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Heading size="text2xl" as="h5" fontFamily="Arimo" mb={4}>
            {title}
          </Heading>
          {error && (
            <Box mt={4} p={4} bg="red.50" borderRadius="md">
              <Text color="red.700">Erreur chargement. Vérifiez l&apos;API Tracking.</Text>
            </Box>
          )}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            {getSearchFilter && (
              <Flex p={4}>
                <InputGroup w={{ base: "100%", sm: "300px" }}>
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </InputGroup>
              </Flex>
            )}
            {isLoading && filteredData.length === 0 ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                <ReactTable columns={columns as ColumnDef<unknown, unknown>[]} data={pagedData} size="sm" />
                {filteredData.length > 0 && (
                  <Pagination
                    pageIndex={pageIndex}
                    setNextPageIndex={handleNextPage}
                    setPrevPageIndex={handlePrevPage}
                    jumpToPage={handleJumpToPage}
                    numberOfPages={numberOfPages}
                  />
                )}
              </>
            )}
            {!isLoading && filteredData.length === 0 && (
              <Box p={8} textAlign="center">
                <Text color="gray.600">Aucune donnée.</Text>
              </Box>
            )}
          </Box>
        </Flex>
      </Flex>
    </>
  );
}

export function textColumn<T>(id: string, header: string, accessor: (row: T) => string | number | null | undefined) {
  const col = createColumnHelper<T>();
  return col.accessor(accessor as any, {
    id,
    header,
    cell: (info) => {
      const v = info.getValue() as string | number | null | undefined;
      return <Text fontSize="sm">{v ?? "—"}</Text>;
    },
  });
}

export function dateColumn<T>(id: string, header: string, accessor: (row: T) => string | null | undefined) {
  const col = createColumnHelper<T>();
  return col.accessor(accessor as any, {
    id,
    header,
    cell: (info) => {
      const raw = info.getValue() as string | number | Date | null | undefined;
      if (!raw) return "—";
      const d = new Date(raw as any);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
    },
  });
}
